import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import {CheckCircle, Edit, ExpandLess, ExpandMore} from '@vedmarket/ui-kit';
import {useTranslation} from 'react-i18next';
import MenuButton from 'components/common/menuButton';
import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';
import {formatHSCode, numberWithSpaces} from 'utils/helpers';
import {getPluralForm} from 'utils/validation';
import {COLORS} from 'config/constants';

import './code-header.scss';

const CustomsCodeHeader = ({
  declaration,
  index,
  isCompleted,
  isCollapsed,
  onToggle,
  onOpenCode,
  onEdit,
}: CustomsCodeHeaderProps) => {
  const {t} = useTranslation();

  const productCount = declaration.product_names.length;
  const productForms: PluralForms = [
    t('declension__text__itemOne'),
    t('declension__text__itemSeveral'),
    t('declension__text__itemMany'),
  ];
  const productLabel = formatDemoCopy(`${productCount} ${getPluralForm(productCount, productForms)}`);

  const statusColor = isCompleted ? COLORS.GREEN : COLORS.STRONG_GRAY;
  const StatusIcon = isCompleted ? CheckCircle : Edit;
  const codeLabel = formatDemoCopy(`${t('calc__text__customs__hsCode')} ${formatHSCode(declaration.code)}`);

  return (
    <div className='customs-code-header'>
      <div className='customs-code-header__identity'>
        <StatusIcon style={{
          color: statusColor,
          width: 24,
          height: 24,
          flexShrink: 0,
        }} />
        <Typography
          component='div'
          variant='subtitle1'
          sx={{
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: 0,
            color: COLORS.BLACK_DARK,
          }}
        >
          {index + 1}. <Typography
            component='button'
            type='button'
            variant='inherit'
            onClick={onOpenCode}
            sx={{
              padding: 0,
              border: 0,
              textDecoration: 'underline dotted',
              textUnderlineOffset: '3px',
              background: 'transparent',
              color: COLORS.BLUE,
              cursor: 'pointer',
              lineHeight: '21px',
              '&:hover': {
                color: COLORS.BLUE_DARK,
              },
            }}
          >
            {codeLabel}
          </Typography>
        </Typography>
      </div>
      <div className='customs-code-header__summary'>
        <Chip
          variant='outlined'
          size='small'
          label={
            <Typography variant='caption' sx={{
              lineHeight: '18px',
              letterSpacing: 0,
              color: COLORS.BLACK_DARK,
            }}>
              {productLabel}
            </Typography>
          }
          sx={{
            height: 24,
            borderColor: COLORS.BORDER_GRAY,
            '& .MuiChip-label': {
              padding: '0 10px',
            },
          }}
        />
        <Typography variant='subtitle1' sx={{
          fontWeight: 400,
          lineHeight: '24px',
          letterSpacing: 0,
          color: COLORS.BLACK_DARK,
          whiteSpace: 'nowrap',
        }}>
          {numberWithSpaces(declaration.subtotal || 0)} USD
        </Typography>
        <Box sx={{
          display: 'flex',
          '& .MuiIconButton-root': {
            padding: 0,
          },
        }}>
          <MenuButton options={[ {title: t('actions__text__edit'), onClick: onEdit} ]} />
        </Box>
        <IconButton
          onClick={onToggle}
          aria-expanded={!isCollapsed}
          aria-label={codeLabel}
          sx={{
            padding: 0,
            color: COLORS.LIGHT_ACTION,
          }}
        >
          {isCollapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </div>
    </div>
  );
};

export default CustomsCodeHeader;
